import * as THREE from "three";
import { FACE, G, Kit, hdr, instance, once, profileSlab, type Surface, type V2 } from "./kit";
import { blobShadow, decalMaterial, glowSprite } from "./fx";
import { wheelObject } from "./parts";
import { DISPLAY_FONT, canvasTex, fitText, roundRectPath } from "./tex";

/**
 * ELON's pickup: you stole his car and he wants it back. A generic angular stainless-steel pickup (flat brushed
 * panels, one wedge from the nose to the roof peak and down to the tail, angular wheel arches, a light bar across
 * the front that flashes his high beams at you, a red bar across the tail, amber hazards), plate "ELON" and a
 * "MY CAR!" sticker on the tail where the chase camera reads them. No brand names or logos. Front = +z.
 * (Code names stay `GasCop` — the chaser used to be the GAS COP.)
 */

const W = 2.08;
const WHEEL_R = 0.46, WHEEL_W = 0.34;
const FRONT_AXLE = 1.55, REAR_AXLE = -1.6;
const ZF = 2.5, ZR = -2.5;                       // nose / tail faces
const NOSE_Y = 1.0;                              // top of the nose: the light bar
const APEX: V2 = [0.1, 1.98];                    // the roof peak
const TAIL_Y = 1.45;
const STEEL: Surface = { r: 0.32, m: 0.9 };      // brushed stainless
const STEEL_TINT = 0xc5cad1, TRIM = 0x17181d, GLASS = 0x141b25;

/** y of the hood/windscreen line (nose → peak) and of the roof/tonneau line (peak → tail) at z. */
const hoodY = (z: number) => NOSE_Y + (ZF - z) * ((APEX[1] - NOSE_Y) / (ZF - APEX[0]));
const roofY = (z: number) => APEX[1] - (APEX[0] - z) * ((APEX[1] - TAIL_Y) / (APEX[0] - ZR));
const HOOD_TILT = Math.atan((APEX[1] - NOSE_Y) / (ZF - APEX[0]));
const ROOF_TILT = Math.atan((APEX[1] - TAIL_Y) / (APEX[0] - ZR));

// ------------------------------------------------------------------------------------------------ plate + sticker

const SIGNS = { w: 512, h: 128, plate: [8, 16, 240, 96], sticker: [264, 4, 240, 120] } as const;
type Rect = readonly [number, number, number, number];

const signsTex = (): THREE.Texture => canvasTex("elon-signs", SIGNS.w, SIGNS.h, (ctx) => {
  // the plate: white, black border and letters, a blue band
  const [px, py, pw, ph] = SIGNS.plate;
  ctx.fillStyle = "#f4f4f0"; ctx.fillRect(px, py, pw, ph);
  ctx.fillStyle = "#1d4fd8"; ctx.fillRect(px, py, 28, ph);
  ctx.strokeStyle = "#111"; ctx.lineWidth = 6; ctx.strokeRect(px + 3, py + 3, pw - 6, ph - 6);
  ctx.fillStyle = "#111"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.font = `900 64px ${DISPLAY_FONT}`;
  ctx.fillText("ELON", px + 14 + pw / 2, py + ph / 2 + 4);
  // the sticker: a speech bubble, "MY CAR!"
  const [sx, sy, sw, sh] = SIGNS.sticker;
  ctx.fillStyle = "#ffd23f";
  roundRectPath(ctx, sx + 6, sy + 6, sw - 12, sh - 34, 18); ctx.fill();
  ctx.beginPath(); ctx.moveTo(sx + 48, sy + sh - 30); ctx.lineTo(sx + 40, sy + sh - 4); ctx.lineTo(sx + 84, sy + sh - 30); ctx.closePath(); ctx.fill();
  ctx.strokeStyle = "#111"; ctx.lineWidth = 5;
  roundRectPath(ctx, sx + 6, sy + 6, sw - 12, sh - 34, 18); ctx.stroke();
  ctx.fillStyle = "#111";
  fitText(ctx, "MY CAR!", sw - 44, 54);
  ctx.fillText("MY CAR!", sx + sw / 2, sy + (sh - 28) / 2 + 4);
});

/** A plane (front = +z) showing one region of the signs canvas (canvas px; textures are flipped, so v runs up). */
function signPlane(w: number, h: number, rect: Rect): THREE.BufferGeometry {
  return once(`g:elonsign:${w}:${h}:${rect.join(",")}`, () => {
    const g = new THREE.PlaneGeometry(w, h);
    const [x, y, rw, rh] = rect;
    const u0 = (x + 1) / SIGNS.w, u1 = (x + rw - 1) / SIGNS.w;
    const v0 = 1 - (y + rh - 1) / SIGNS.h, v1 = 1 - (y + 1) / SIGNS.h;
    const uv = g.getAttribute("uv") as THREE.BufferAttribute;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, u0 + uv.getX(i) * (u1 - u0), v0 + uv.getY(i) * (v1 - v0));
    return g;
  });
}

// ------------------------------------------------------------------------------------------------ the pickup

export interface GasCopParts {
  /** the whole pickup: add it to the chaser group */
  root: THREE.Group;
  /** named wheel-front-left … (left = +x); roll them with rotation.x */
  wheels: THREE.Object3D[];
  length: number;
  height: number;
}

/**
 * `buildGasCop()` → { root, wheels, length, height } — the same shape as the scene's Vehicle. Origin = centre of
 * the footprint, y = 0 on the road, front = +z. `animateProp(root, t)` flashes the front light bar and the amber
 * hazards (set root.userData.fast = true while hunting). ~3k triangles.
 */
export function buildGasCop(): GasCopParts {
  const root = instance("gasCop", () => {
    const kit = new Kit();
    /** an angular wheel arch around an axle, rear → over → front */
    const arch = (cz: number): V2[] => [[cz - 0.62, 0.48], [cz - 0.42, 1.03], [cz + 0.42, 1.03], [cz + 0.62, 0.48]];
    // the wedge: one flat-sided steel slab, nose → peak → tail
    const body: V2[] = [[ZR, 0.5], ...arch(REAR_AXLE), ...arch(FRONT_AXLE), [ZF, 0.5], [ZF, NOSE_Y], APEX, [ZR, TAIL_Y]];
    kit.add(profileSlab(body, W, 0.05, { curveSegments: 1, bevelSegments: 1 }), STEEL, STEEL_TINT);
    kit.add(G.box(W - 0.3, 0.12, ZF - ZR - 0.4), "rubber", 0x0f1013, { p: [0, 0.42, 0] });                 // floor pan
    // side glass (a dark slab a hair wider than the body), split by a steel pillar
    kit.add(profileSlab([[-1.1, 1.38], [0.85, 1.38], [0.17, 1.9], [-1.1, 1.7]], W + 0.014, 0.0, { curveSegments: 1 }), "glass", GLASS);
    kit.add(G.box(W + 0.02, 0.48, 0.07), STEEL, STEEL_TINT, { p: [0, 1.62, -0.28] });
    // windscreen on the hood line, glass roof behind the peak, the tonneau seam
    kit.add(G.box(W - 0.24, 0.012, 0.85 / Math.cos(HOOD_TILT)), "glass", GLASS, { p: [0, hoodY(0.625) + 0.008, 0.625], r: [HOOD_TILT, 0, 0] });
    kit.add(G.box(W - 0.3, 0.012, 1.05 / Math.cos(ROOF_TILT)), "glass", GLASS, { p: [0, roofY(-0.525) + 0.008, -0.525], r: [-ROOF_TILT, 0, 0] });
    kit.add(G.box(W - 0.1, 0.012, 0.03), "plastic", TRIM, { p: [0, roofY(-1.25) + 0.007, -1.25], r: [-ROOF_TILT, 0, 0] });
    // dark sills and bumpers, mirror stubs
    kit.add(G.box(W + 0.02, 0.12, 1.8), "plastic", TRIM, { p: [0, 0.55, -0.03] });
    kit.add(G.chamfer(W + 0.04, 0.2, 0.2, 0.03), "plastic", TRIM, { p: [0, 0.56, ZF + 0.02] });
    kit.add(G.chamfer(W + 0.04, 0.2, 0.2, 0.03), "plastic", TRIM, { p: [0, 0.56, ZR - 0.02] });
    for (const x of [-1, 1]) kit.add(G.chamfer(0.18, 0.1, 0.14, 0.02), STEEL, STEEL_TINT, { p: [x * (W / 2 + 0.07), 1.45, 0.75] });
    // lights: a faint always-on strip under the front bar, the red bar across the tail, amber corners
    kit.add(G.box(W - 0.1, 0.02, 0.03), "glow", hdr(0xf4f8ff, 0.9), { p: [0, NOSE_Y - 0.05, ZF + 0.004] });
    kit.add(G.box(W - 0.12, 0.05, 0.03), "glow", hdr(0xff1e30, 1.5), { p: [0, TAIL_Y - 0.06, ZR - 0.01] });
    for (const x of [-1, 1]) {
      kit.add(G.box(0.14, 0.08, 0.03), "glow", hdr(0xffa21f, 1.3), { p: [x * (W / 2 - 0.12), TAIL_Y - 0.15, ZR - 0.012] });
      kit.add(G.box(0.14, 0.06, 0.03), "glow", hdr(0xffa21f, 1.2), { p: [x * (W / 2 - 0.1), 0.78, ZF + 0.008] });
    }
    // plate "ELON" front and back, the "MY CAR!" sticker on the tail
    const signs = decalMaterial("elon-signs", signsTex(), { roughness: 0.35 });
    kit.add(signPlane(0.62, 0.25, SIGNS.plate), signs, 0xffffff, { p: [0, 0.82, ZR - 0.006], r: FACE.front });
    kit.add(signPlane(0.62, 0.25, SIGNS.plate), signs, 0xffffff, { p: [0, 0.72, ZF + 0.006], r: FACE.back });
    kit.add(signPlane(0.5, 0.25, SIGNS.sticker), signs, 0xffffff, { p: [-0.58, 1.12, ZR - 0.006], r: FACE.front });
    const shell = kit.build({ name: "body" });

    const car = new THREE.Group();
    car.name = "gasCop";
    car.userData.anim = "cop";
    car.add(shell, blobShadow(2.8, ZF - ZR + 0.8));
    // the front light bar in two halves (own meshes so they can flash) + their glows, and the amber hazard glows
    // at the tail: lampA* and lampB* flash in turn
    const barGeo = G.box(W / 2 - 0.08, 0.05, 0.04);
    for (const [name, side] of [["lampA", 1], ["lampB", -1]] as [string, number][]) {
      const lens = new THREE.Mesh(barGeo, copLensMaterial(0xf4f8ff, false));
      lens.name = name;
      lens.position.set(side * (W / 4), NOSE_Y - 0.012, ZF + 0.012);
      lens.userData.color = 0xf4f8ff;
      const flash = glowSprite(0xf4f8ff, 2.4, 0.9);
      flash.name = `${name}Flash`;
      flash.position.set(side * (W / 4), NOSE_Y, ZF + 0.2);
      const hazard = glowSprite(0xffa21f, 1.5, 0.85);
      hazard.name = `${name}Flash`;
      hazard.position.set(side * (W / 2 - 0.12), TAIL_Y - 0.15, ZR - 0.12);
      car.add(lens, flash, hazard);
    }
    // wheels: spinning objects
    for (const [z, zName] of [[FRONT_AXLE, "front"], [REAR_AXLE, "rear"]] as [number, string][]) {
      for (const [x, side] of [[1, "left"], [-1, "right"]] as [number, string][]) {
        const wheel = wheelObject(`wheel-${zName}-${side}`, WHEEL_R, WHEEL_W, x as 1 | -1, 0x9aa1ab);
        wheel.position.set(x * (W / 2 - 0.19), WHEEL_R, z);
        car.add(wheel);
      }
    }
    return car;
  });
  const wheels: THREE.Object3D[] = [];
  root.traverse((o) => { if (o.name.startsWith("wheel-")) wheels.push(o); });
  return { root, wheels, length: ZF - ZR + 0.2, height: APEX[1] };
}

/** Lit / unlit lens materials (shared; the flash swaps them per lens). */
export function copLensMaterial(color: number, on: boolean): THREE.Material {
  return once(`mat:coplens:${color}:${on}`, () => on
    ? new THREE.MeshBasicMaterial({ color: hdr(color, 2.4), toneMapped: false })
    : new THREE.MeshStandardMaterial({ color: new THREE.Color(color).multiplyScalar(0.35), roughness: 0.15, metalness: 0.1, emissive: color, emissiveIntensity: 0.18 }));
}

/** Flash pattern: the two halves of the light bar (and the hazards) alternate with a double strobe; faster while hunting. */
export function animateGasCop(root: THREE.Object3D, t: number, fast = false): void {
  const rate = fast ? 2.6 : 1.6;
  const phase = (t * rate) % 1;
  const strobe = (p: number) => (p < 0.12 || (p > 0.2 && p < 0.32));
  const aOn = phase < 0.5 && strobe(phase * 2);
  const bOn = phase >= 0.5 && strobe((phase - 0.5) * 2);
  for (const child of root.children) {
    if (child.name === "lampA" || child.name === "lampB") {
      const on = child.name === "lampA" ? aOn : bOn;
      (child as THREE.Mesh).material = copLensMaterial(child.userData.color as number, on);
    } else if (child.name === "lampAFlash") child.visible = aOn;
    else if (child.name === "lampBFlash") child.visible = bOn;
  }
}
